import React, { useMemo, useState } from "react";
import { fmtDate, cellClass } from "../lib/format";

/**
 * Mini heatmap:
 *   - X (columns) = expirations, earliest left → latest right
 *   - Y (rows)    = strikes, highest top → lowest bottom
 *
 * Click rules:
 *   - If main table filter is "ALL" → just scroll to the row
 *   - Else → switch filter to that expiration AND scroll
 */
export const MiniHeatmap = ({
  candidates,
  underlying,
  position,
  selectedCandidate,
  onPick,
  expFilter,
  setExpFilter,
}) => {
  const [hover, setHover] = useState(null);

  const { strikes, expirations, byKey } = useMemo(() => {
    if (!candidates) return { strikes: [], expirations: [], byKey: new Map() };
    const sSet = new Set();
    const eSet = new Set();
    const map = new Map();
    for (const c of candidates) {
      sSet.add(c.strike);
      eSet.add(c.expiration);
      map.set(`${c.strike}|${c.expiration}`, c);
    }
    return {
      strikes: [...sSet].sort((a, b) => a - b),
      expirations: [...eSet].sort(),
      byKey: map,
    };
  }, [candidates]);

  if (!candidates || candidates.length === 0) return null;

  const underlyingRowIdx = strikes.findIndex((s) => s >= underlying);

  const scrollToCandidateRow = (c) => {
    requestAnimationFrame(() => {
      const sel = `[data-testid="candidate-row-${c.strike}-${c.expiration}"]`;
      const el = document.querySelector(sel);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  };

  const handleClick = (c) => {
    if (!c) return;
    onPick?.(c);
    if (expFilter && expFilter !== "ALL" && expFilter !== c.expiration) {
      // Switch the main table filter, then scroll once the new rows are rendered.
      setExpFilter?.(c.expiration);
      setTimeout(() => scrollToCandidateRow(c), 60);
    } else {
      scrollToCandidateRow(c);
    }
  };

  // Compact expiration label using US-Eastern calendar conventions.
  const shortExp = (iso) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
    const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(iso);
    const mon = d.toLocaleDateString("en-US", { month: "short" });
    const day = d.getDate().toString().padStart(2, "0");
    return { m: mon, day };
  };

  // Reserve a fixed-height row for the hover info to avoid layout flicker.
  return (
    <div className="panel flex flex-col flex-1 min-h-0 w-full" data-testid="mini-heatmap-panel">
      <div className="panel-h flex-col items-start gap-1 flex-shrink-0">
        <div className="flex w-full items-baseline justify-between">
          <div className="label-cap">Heatmap Overview</div>
          <div className="text-[10px] font-mono text-[#71717A]">
            {expirations.length} exps × {strikes.length} strikes
          </div>
        </div>
        <div className="h-[28px] w-full font-mono text-[10px] leading-tight">
          {hover ? (
            <>
              <div className="text-white">
                ${hover.strike.toFixed(2)} · {fmtDate(hover.expiration)}
              </div>
              <div className="text-[#A1A1AA]">
                {hover.credit_pct >= 0 ? "+" : ""}
                {hover.credit_pct.toFixed(2)}% · score {hover.score} · {hover.category.replace("_", " ")}
              </div>
            </>
          ) : (
            <div className="text-[#71717A]">hover a cell · click to jump</div>
          )}
        </div>
      </div>

      <div className="p-3 overflow-auto flex-1 min-h-0" data-testid="mini-heatmap-grid">
        <div className="inline-block">
          {/* X-axis: stacked month/day labels above the grid */}
          <div className="flex" style={{ marginLeft: 52 }}>
            {expirations.map((e) => {
              const { m, day } = shortExp(e);
              return (
                <div
                  key={e}
                  className="mini-xlabel"
                  title={e}
                  onMouseEnter={() => {
                    // also highlight on column hover? skip to keep things simple
                  }}
                >
                  <span className="mini-xlabel-mon">{m}</span>
                  <span className="mini-xlabel-day">{day}</span>
                </div>
              );
            })}
          </div>

          {strikes.map((strike, rIdx) => {
            const isUnderlyingBoundary = rIdx === underlyingRowIdx;
            return (
              <div key={strike} className="flex items-center gap-1 mb-px relative">
                <div className="font-mono text-[9px] text-[#A1A1AA] w-[48px] text-right pr-1 truncate">
                  ${strike.toFixed(0)}
                </div>
                <div className="flex relative">
                  {isUnderlyingBoundary && (
                    <>
                      {/* Horizontal line — sits exactly in the 1px gap above this row */}
                      <div
                        className="absolute left-0 right-0 pointer-events-none bg-[#FF9F0A]"
                        style={{ top: "-1px", height: "1px", zIndex: 6 }}
                      />
                      {/* Label — positioned so its vertical center aligns with the line */}
                      <span
                        className="absolute text-[8px] font-mono text-[#FF9F0A] leading-none pointer-events-none whitespace-nowrap"
                        style={{ top: "-4px", left: "calc(100% + 4px)", zIndex: 6 }}
                      >
                        ${underlying.toFixed(0)}
                      </span>
                    </>
                  )}
                  {expirations.map((exp) => {
                    const c = byKey.get(`${strike}|${exp}`);
                    const isSelected =
                      selectedCandidate &&
                      selectedCandidate.strike === strike &&
                      selectedCandidate.expiration === exp;
                    const isCurrent =
                      Math.abs(strike - position.strike) < 0.001 && exp === position.expiration;
                    const cls = c ? cellClass(c.category) : "cell-empty";
                    return (
                      <button
                        key={exp}
                        data-testid={`mini-cell-${strike}-${exp}`}
                        onMouseEnter={() => setHover(c || null)}
                        onMouseLeave={() => setHover(null)}
                        onClick={() => handleClick(c)}
                        disabled={!c}
                        className={`${cls} mini-cell ${isSelected ? "mini-cell-selected" : ""} ${isCurrent ? "mini-cell-current" : ""}`}
                        title={c ? `$${strike} · ${fmtDate(exp)} · ${c.category}` : ""}
                      />
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="px-3 py-2 border-t border-[#27272A] flex items-center justify-between text-[9px] text-[#71717A] flex-shrink-0">
        <span>← earlier</span>
        <span>strike low→high ↓ · time →</span>
        <span>later →</span>
      </div>
    </div>
  );
};
